import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { clientLogin } from "../../../api/clientAuthApi";
import { ClientAuthProvider, useClientAuth } from "../../../auth/ClientAuthProvider";
import { useClientPortalPaths } from "../../../hooks/useClientPortalBase";
import { MIDPOINT_LOGO_WHITE_SRC } from "../../../constants/midpointBrand";
import "../../../styles/midpointMediaAdminLogin.css";

const ASSET_BASE = `${process.env.PUBLIC_URL || ""}/assets/midpoint-media`;
const LOGIN_VISUAL = `${ASSET_BASE}/signal_intel_case_study.png`;
const LOGIN_PLATFORM = `${ASSET_BASE}/automap_case_study1.png`;

function MidpointMediaAdminLoginForm() {
  const navigate = useNavigate();
  const { isAuthenticated, refresh } = useClientAuth();
  const paths = useClientPortalPaths();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to={paths.dashboard} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSubmitting(true);
    try {
      await clientLogin(email, password);
      await refresh();
      navigate(paths.dashboard);
    } catch (error) {
      setErrorMessage(error.message || "Sign-in failed. Check your work email and password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mm-admin-login">
      <aside className="mm-admin-login__brand" aria-label="Midpoint Media personnel portal">
        <div className="mm-admin-login__brand-media" aria-hidden="true">
          <img src={LOGIN_VISUAL} alt="" className="mm-admin-login__brand-photo" />
          <img src={LOGIN_PLATFORM} alt="" className="mm-admin-login__brand-inset" />
        </div>
        <div className="mm-admin-login__brand-content">
          <img
            src={MIDPOINT_LOGO_WHITE_SRC}
            alt="Midpoint Media"
            className="mm-admin-login__logo"
          />
          <p className="mm-admin-login__eyebrow">Operations portal</p>
          <h1>For Midpoint Media personnel only</h1>
          <p className="mm-admin-login__lead">
            Secure access for employees working CRM, hiring, infrastructure, and delivery. Customer
            and public visitors should use the Midpoint Media website instead.
          </p>
          <ul className="mm-admin-login__bullets">
            <li>Company email accounts only</li>
            <li>Hiring applications and people coverage</li>
            <li>Infrastructure and project status</li>
          </ul>
        </div>
      </aside>

      <main className="mm-admin-login__panel">
        <div className="mm-admin-login__card">
          <p className="mm-admin-login__panel-kicker">Employee sign-in</p>
          <h2>Sign in to Operations</h2>
          <p className="mm-admin-login__panel-lead">
            Use the Midpoint Media credentials issued to your team. Unauthorized access is not
            permitted.
          </p>

          <form className="mm-admin-login__form" onSubmit={handleSubmit}>
            <label>
              Work email
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="username"
                placeholder="you@midpointmedia.com"
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="current-password"
              />
            </label>
            {errorMessage ? (
              <p className="mm-admin-login__error" role="alert">
                {errorMessage}
              </p>
            ) : null}
            <button type="submit" className="mm-admin-login__submit" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mm-admin-login__footnote">
            Not Midpoint Media staff?{" "}
            <Link to="/v/midpoint-media">Return to the public site</Link>
          </p>
        </div>
      </main>
    </div>
  );
}

export default function MidpointMediaAdminLoginPage() {
  return (
    <ClientAuthProvider>
      <MidpointMediaAdminLoginForm />
    </ClientAuthProvider>
  );
}
